
import { db, collection, addDoc, updateDoc, deleteDoc, doc, getDoc } from "../../firebaseClient";
import type { ContractDoc } from "../types/legalOps";
import { logActivity } from "./activityLog";
import { syncContractExpiryEvents, deleteContractCalendarEvents } from "./calendarService";

const CONTRACT_COLLECTION = "legal_contracts";

export async function createContract(payload: ContractDoc) {
  const colRef = collection(db, CONTRACT_COLLECTION);
  const { id, ...data } = payload;
  
  const docRef = await addDoc(colRef, {
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Sync Calendar
  await syncContractExpiryEvents({ 
      id: docRef.id, 
      title: payload.title,
      contractNo: payload.contractNo,
      periodEnd: payload.periodEnd,
      signDate: payload.signDate || payload.periodStart, 
      status: payload.status
  });

  await logActivity({
    module: "contract",
    action: "create",
    entityId: docRef.id,
    entityType: "Contract",
    entityTitle: payload.title,
    actorName: payload.lastUpdatedBy,
    details: `Created contract ${payload.contractNo}`
  });

  return docRef.id;
}

export function listenContracts(
  onChange: (contracts: ContractDoc[]) => void,
  onError?: (error: Error) => void
) {
  const col = db.collection(CONTRACT_COLLECTION);
  return col.onSnapshot(
    (snap: any) => {
      const list = snap.docs.map((d: any) => ({
        id: d.id,
        ...(d.data() as Omit<ContractDoc, "id">),
      }));
      list.sort((a: any, b: any) => {
          const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tB - tA;
      });
      onChange(list);
    },
    (err: any) => {
      console.error("[ContractService] Listener failed:", err);
      if (onError) onError(err);
    }
  );
}

export async function updateContract(id: string, patch: Partial<ContractDoc>) {
  const docRef = doc(db, CONTRACT_COLLECTION, id);
  await updateDoc(docRef, {
    ...patch,
    updatedAt: new Date().toISOString(),
  });

  // Re-sync calendar events
  const docSnap = await getDoc(docRef);
  if (docSnap.exists) {
      const data = docSnap.data() as any;
      await syncContractExpiryEvents({ 
          id: id, 
          title: data.title,
          contractNo: data.contractNo,
          periodEnd: data.periodEnd,
          signDate: data.signDate || data.periodStart,
          status: data.status
      });
  }
    
  await logActivity({
      module: "contract",
      action: "update",
      entityId: id,
      entityType: "Contract",
      entityTitle: patch.title || "Contract",
      actorName: patch.lastUpdatedBy || "User",
      details: "Updated contract details"
  });
}

export async function deleteContract(id: string, contractNo: string) {
  const docRef = doc(db, CONTRACT_COLLECTION, id);
  await deleteDoc(docRef);

  // Clean up Calendar Events
  await deleteContractCalendarEvents(id);
  
  await logActivity({
    module: "contract",
    action: "delete",
    entityId: id,
    entityType: "Contract",
    details: `Deleted contract ${contractNo}`
  });
}

export async function seedDemoContracts() {
  alert("Demo function not implemented in production mode");
}
